import * as Sentry from '@sentry/nextjs'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { Component as ReactComponent, useEffect, useMemo } from 'react'
import { Button, Flex, Text } from 'theme-ui'
import MainLayout from '@/components/Commons/Layout'
import { AnalyticsHead, AnalyticsBody } from '@/components/scripts'
import { getCampaignFonts } from '@/components/scripts/fonts'
import { DEFAULT_LOCALE } from '@/constants/locales'
import { getCurrentCampaign } from '@/utils/get-current-campaign'
import 'react-datepicker/dist/react-datepicker.css'
import 'setimmediate'

// @ts-expect-error polyfill
global.setImmediate ??= setTimeout

const DEFAULT_TITLE = 'TaylorMade'
const DEFAULT_OG_TAGS = {
  url: '',
  type: 'website',
  title: DEFAULT_TITLE,
  description: '',
  card: 'summary_large_image',
  image: '',
}

const errorStyles: StyleObject = {
  container: {
    minHeight: '100dvh',
    width: '100%',
    p: ['32px 20px', '48px'],
    background: '#0c140c',
    color: '#ffffff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  panel: {
    width: '100%',
    maxWidth: '680px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '18px',
    borderLeft: '4px solid #f37021',
    p: ['0 0 0 20px', '0 0 0 28px'],
  },
  title: {
    m: 0,
    fontFamily: 'din-bold-cn',
    fontSize: ['42px', '60px'],
    lineHeight: 0.95,
    textTransform: 'uppercase',
  },
  message: {
    m: 0,
    maxWidth: '540px',
    fontFamily: 'regular',
    fontSize: ['16px', '18px'],
    lineHeight: 1.5,
    color: 'rgba(255, 255, 255, 0.82)',
  },
  button: {
    mt: '8px',
    px: '24px',
    py: '12px',
    color: '#ffffff',
    border: '1px solid rgba(255, 255, 255, 0.75)',
    borderRadius: '40px',
    background: 'transparent',
    fontFamily: 'din-bold-cn',
    fontSize: '18px',
    textTransform: 'uppercase',
    cursor: 'pointer',

    '&:hover': {
      background: '#f37021',
      borderColor: '#f37021',
    },
  },
}

class AppErrorBoundary extends ReactComponent<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    Sentry.captureException(error)
  }

  render() {
    if (this.state.hasError) {
      return (
        <Flex sx={errorStyles.container}>
          <Flex sx={errorStyles.panel}>
            <Text as="h1" sx={errorStyles.title}>
              Something went wrong
            </Text>
            <Text as="p" sx={errorStyles.message}>
              The application hit an unexpected problem. Reload the page or
              return to the home page.
            </Text>
            <Button
              type="button"
              sx={errorStyles.button}
              onClick={() => {
                window.location.href = '/'
              }}
            >
              Go to home page
            </Button>
          </Flex>
        </Flex>
      )
    }

    return this.props.children
  }
}

const TaylorMadeApp = ({
  Component,
  pageProps,
}: NextAppProps<{ locale: Locale; isSsrMobile: boolean }>) => {
  const router = useRouter()

  const {
    theme,
    meta,
    name: campaign,
    gtmCode,
    ogTags,
  } = getCurrentCampaign(router.route, router.asPath)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const locale = pageProps.locale ?? DEFAULT_LOCALE
  const fonts = useMemo(
    () => (campaign ? getCampaignFonts(campaign) : []),
    [campaign],
  )
  const og = useMemo(() => {
    const tags = ogTags as Nullable<Record<string, typeof DEFAULT_OG_TAGS>>

    return tags?.[locale] ?? tags?.[DEFAULT_LOCALE] ?? DEFAULT_OG_TAGS
  }, [locale, ogTags])
  const pageTitle =
    (meta as Nullable<Record<string, { title?: string }>>)?.[locale]?.title ??
    (meta as Nullable<Record<string, { title?: string }>>)?.[DEFAULT_LOCALE]
      ?.title ??
    DEFAULT_TITLE

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <link rel="shortcut icon" href="/static/images/favicon.png" />
        {fonts}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0"
        />
        <meta property="og:url" content={og.url} />
        <meta property="og:type" content={og.type} />
        <meta property="og:title" content={og.title} />
        <meta property="og:description" content={og.description} />
        <meta name="twitter:card" content={og.card} />
        <meta property="og:image" content={og.image} />
        {gtmCode && <AnalyticsHead code={gtmCode} />}
      </Head>
      <MainLayout
        theme={theme}
        isSsrMobile={pageProps.isSsrMobile}
        path={router?.route?.split('/').at(-1)}
        campaign={campaign}
      >
        <AppErrorBoundary>
          <Component {...pageProps} />
        </AppErrorBoundary>
        {gtmCode && <AnalyticsBody code={gtmCode} />}
      </MainLayout>
    </>
  )
}

export default TaylorMadeApp
